import { useCallback, useEffect, useMemo, useRef } from 'react';
import { stringifySearchWith, useNavigate, useRouterState } from '@tanstack/react-router';

/**
 * Workspace state in the URL.
 *
 * Which record is open, what is being searched for, which filters are applied
 * and which page you are on are *navigational* facts, not component state. In
 * `useState` they die on refresh and cannot be sent to a colleague — "the
 * flagged attendance for Paolo on the 4th" becomes a set of instructions rather
 * than a link.
 *
 * Unsaved form data deliberately stays out. A half-typed reason in the address
 * bar is a privacy problem and a browser-history problem at once.
 */

/**
 * How the query string is read and written: plain text, both ways.
 *
 * The router's default JSON-encodes any string that would parse as JSON, so
 * the value it reads back keeps its type — and the audit log's page size went
 * into the address bar as `pageSize=%22200%22`, a quoted number. Every value
 * here is a string (see `passthroughSearch`), so there is no type to keep:
 * `200` is written as 200. A quoted value from a link made before the change
 * reads as the text inside the quotes, so old links still open the same view.
 */
export function parseSearch(searchStr: string): Record<string, unknown> {
  /*
   * Read with URLSearchParams, not the router's decoder: that one turns
   * `q=12345` into the number 12345 and `q=true` into a boolean before any
   * parser sees it, and the workspace — which reads text — dropped the search
   * and listed every record for "12345", "2026" or "34000". Every value is
   * text here, exactly as typed.
   */
  const params = new URLSearchParams(searchStr.startsWith('?') ? searchStr.slice(1) : searchStr);
  const query: Record<string, unknown> = {};
  for (const [key, raw] of params) {
    const value = unquote(raw);
    const known = query[key];
    if (known === undefined) query[key] = value;
    else query[key] = Array.isArray(known) ? [...(known as unknown[]), value] : [known, value];
  }
  return query;
}

function unquote(value: string): string {
  if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
    try {
      const parsed: unknown = JSON.parse(value);
      if (typeof parsed === 'string') return parsed;
    } catch {
      // Not a quoted string after all: kept as typed.
    }
  }
  return value;
}

export const stringifySearch = stringifySearchWith(JSON.stringify);

export interface WorkspaceState {
  /** Free-text search. */
  q: string;
  /** Named filter values, e.g. `{ status: 'flagged' }`. */
  filters: Record<string, string>;
  /** Saved view key. */
  view: string;
  /** Selected record id, so a refresh reopens the same inspector. */
  selected: string | null;
  /** Active tab inside a record workspace. */
  tab: string | null;
  page: number;
  pageSize: number;
  sortBy: string | null;
  sortDir: 'asc' | 'desc';
}

const DEFAULTS: WorkspaceState = {
  q: '',
  filters: {},
  view: '',
  selected: null,
  tab: null,
  page: 1,
  pageSize: 25,
  sortBy: null,
  sortDir: 'asc',
};

/** Keys the workspace owns; anything else in the query string is left alone. */
const RESERVED = new Set(['q', 'view', 'selected', 'tab', 'page', 'pageSize', 'sortBy', 'sortDir']);

function parse(search: Record<string, unknown>): WorkspaceState {
  const filters: Record<string, string> = {};
  for (const [key, value] of Object.entries(search)) {
    if (RESERVED.has(key)) continue;
    if (typeof value === 'string' && value.length > 0) filters[key] = value;
  }

  const page = Number(search.page);
  const pageSize = Number(search.pageSize);

  return {
    q: typeof search.q === 'string' ? search.q : DEFAULTS.q,
    filters,
    view: typeof search.view === 'string' ? search.view : DEFAULTS.view,
    selected: typeof search.selected === 'string' ? search.selected : null,
    tab: typeof search.tab === 'string' ? search.tab : null,
    page: Number.isFinite(page) && page > 0 ? page : DEFAULTS.page,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : DEFAULTS.pageSize,
    sortBy: typeof search.sortBy === 'string' ? search.sortBy : null,
    sortDir: search.sortDir === 'desc' ? 'desc' : 'asc',
  };
}

/** Drop empty values so the address bar stays readable. */
function serialise(state: WorkspaceState): Record<string, string> {
  const next: Record<string, string> = {};
  if (state.q) next.q = state.q;
  if (state.view) next.view = state.view;
  if (state.selected) next.selected = state.selected;
  if (state.tab) next.tab = state.tab;
  if (state.page > 1) next.page = String(state.page);
  if (state.pageSize !== DEFAULTS.pageSize) next.pageSize = String(state.pageSize);
  if (state.sortBy) {
    next.sortBy = state.sortBy;
    next.sortDir = state.sortDir;
  }
  for (const [key, value] of Object.entries(state.filters)) {
    if (value && value !== 'all') next[key] = value;
  }
  return next;
}

export interface WorkspaceStateApi extends WorkspaceState {
  /** Merge a partial change. Anything that narrows results resets the page. */
  set: (patch: Partial<WorkspaceState>) => void;
  setFilter: (key: string, value: string) => void;
  clearFilters: () => void;
  select: (id: string | null) => void;
  /** True when a search or filter is narrowing the result set. */
  isFiltered: boolean;
}

export function useWorkspaceState(): WorkspaceStateApi {
  const navigate = useNavigate();
  const search = useRouterState({
    select: (routerState) => routerState.location.search as unknown as Record<string, unknown>,
  });

  const state = useMemo(() => parse(search ?? {}), [search]);

  const set = useCallback(
    (patch: Partial<WorkspaceState>) => {
      const next = { ...state, ...patch, filters: { ...state.filters, ...(patch.filters ?? {}) } };

      // Narrowing the result set while on page 4 would request page 4 of a
      // smaller result and render an empty table, which reads as "no matches"
      // and is not.
      const narrowed =
        patch.q !== undefined || patch.filters !== undefined || patch.view !== undefined;
      if (narrowed && patch.page === undefined) next.page = 1;

      /**
       * Opening a record is a place you can go Back from; typing in a search
       * box is not. Pushing every keystroke would bury the previous page under
       * a dozen history entries, and replacing the record selection would make
       * Back leave the module entirely rather than close the panel.
       */
      const opensRecord = patch.selected !== undefined && patch.selected !== state.selected;

      void navigate({ to: '.', search: serialise(next) as never, replace: !opensRecord });
    },
    [navigate, state],
  );

  return {
    ...state,
    set,
    setFilter: (key, value) => set({ filters: { [key]: value } }),
    clearFilters: () => {
      void navigate({
        to: '.',
        search: serialise({ ...state, q: '', filters: {}, view: '', page: 1 }) as never,
        replace: true,
      });
    },
    select: (id) => set({ selected: id, tab: id === null ? null : state.tab }),
    isFiltered:
      state.q.trim().length > 0 ||
      state.view.length > 0 ||
      Object.values(state.filters).some((value) => value && value !== 'all'),
  };
}

/* -------------------------------------------------------------------------- */
/* Remembered views                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Bring a page's last search and filters back when it is opened bare.
 *
 * The URL already carries them, so Back and Forward and a refresh keep the
 * view. Arriving from the sidebar does not: its link has no query string, and
 * the audit filters somebody spent a minute narrowing were gone the moment
 * they looked something up elsewhere and came back. Opened with no query
 * string, the page is sent — replacing the history entry, not adding one —
 * to the query it last had in this tab. A link that names its own query (a
 * record's "view in audit log") is never overridden.
 *
 * The open record and tab are not remembered: coming back to a list is not
 * coming back to a panel. Kept in sessionStorage, per signed-in user, so it
 * ends with the tab and never follows a different person.
 */
const NOT_REMEMBERED = new Set(['selected', 'tab']);

function rememberedKey(name: string, userId: string | null): string {
  return `micarahr.view.${userId ?? 'anonymous'}.${name}`;
}

export function readRememberedView(name: string, userId: string | null): Record<string, string> {
  try {
    const raw = window.sessionStorage.getItem(rememberedKey(name, userId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const view: Record<string, string> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'string' && value.length > 0 && !NOT_REMEMBERED.has(key)) {
        view[key] = value;
      }
    }
    return view;
  } catch {
    // Storage blocked or garbled: the page opens unfiltered, as it always did.
    return {};
  }
}

function writeRememberedView(name: string, userId: string | null, search: Record<string, unknown>) {
  const view: Record<string, string> = {};
  for (const [key, value] of Object.entries(search)) {
    if (NOT_REMEMBERED.has(key)) continue;
    if (typeof value === 'string' && value.length > 0) view[key] = value;
    else if (typeof value === 'number' && Number.isFinite(value)) view[key] = String(value);
    else if (typeof value === 'boolean') view[key] = String(value);
  }
  try {
    window.sessionStorage.setItem(rememberedKey(name, userId), JSON.stringify(view));
  } catch {
    // Storage blocked: the URL still carries the view for Back and refresh.
  }
}

export function useRememberedView(name: string | undefined, userId: string | null): void {
  const navigate = useNavigate();
  const location = useRouterState({ select: (routerState) => routerState.location });
  const search = location.search as unknown as Record<string, unknown>;
  const pathname = location.pathname;
  // Decided once, on arrival: afterwards an empty query string is somebody
  // clearing the filters, which is itself worth remembering.
  const arrival = useRef<'pending' | 'restoring' | 'settled'>('pending');
  /*
   * The page this view belongs to. Leaving it, the router moves to the next
   * address while this page is still mounted, and its query — the sidebar
   * link's empty one — was written here as though the filters had been
   * cleared: the view was wiped on the way out, and coming back found nothing
   * to restore. Only the page's own address is ever remembered.
   */
  const home = useRef<string | null>(null);

  useEffect(() => {
    if (!name) return;
    if (home.current === null) home.current = pathname;
    if (pathname !== home.current) return;
    if (arrival.current === 'pending') {
      const bare = Object.keys(search ?? {}).length === 0;
      const saved = bare ? readRememberedView(name, userId) : {};
      if (Object.keys(saved).length > 0) {
        arrival.current = 'restoring';
        void navigate({ to: '.', search: saved as never, replace: true });
        return;
      }
      arrival.current = 'settled';
    } else if (arrival.current === 'restoring') {
      // The empty query the page arrived with, seen once more before the
      // restored one lands, must not overwrite what is being restored.
      if (Object.keys(search ?? {}).length === 0) return;
      arrival.current = 'settled';
    }
    writeRememberedView(name, userId, search ?? {});
  }, [name, userId, search, pathname, navigate]);
}
